import { Col, Row, theme, Typography } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import { actSchema, EntityTypes } from "@vzbl/integrations";
import { FixedSizeList as List, ListChildComponentProps } from "react-window";
import { AutoSizer } from "react-virtualized";
import { GithubIcon } from "src/components/GithubIcon";
import { coerceArray } from "src/utils/collections";
import { mono } from "src/fonts";
import { useApplicationContext } from "src/hooks/useApplication";
import { useEntityContext } from "../useEntityContext";
import { VzblTag } from "../components/VzblTag";
import { TabbedWork, TabbedWorkItems } from "../components/TabbedWork";
import { generateWorkTabs } from "../components/worktabs";
import { History, HistoryComponent } from "../History";
import { Insights } from "../Insights";
import { RelatedEntity, RelatedVzblTag } from "../RelatedEntity";
import { SentencesList } from "./components/SentencesList";
import { ProgressMetric } from "./components/ProgressMetric";
import { MetricTag, SummaryTag } from "./components/Tags";
import { SummaryPanel } from "./components/layout/SummaryPanel";
import { HeaderAttribute } from "./components/HeaderAttributes";
import { EntityTabs } from "./components/EntityTabs";
import { InsightsPanel } from "./components/layout/InsightsPanel";
import { EntityScreen } from "./components/layout/Screen";
import { HeaderPanel } from "./components/layout/HeaderPanel";
import { ActionCard } from "./components/ActionCard";
import { TagsList } from "../components/TagsList";
import { Spacer } from "../components/Spacer";
import { parseDiff, Diff, Hunk } from "react-diff-view";
import "react-diff-view/style/index.css";
import { memo } from "react";

const formatPatchToUnifiedDiff = (
  files: Array<{ filename: string; patch?: string }>
) => {
  return files
    .filter((file) => file.patch) // Only process files that have patches
    .map(
      (file) =>
        `diff --git a/${file.filename} b/${file.filename}
--- a/${file.filename}
+++ b/${file.filename}
${file.patch}`
    )
    .join("\n");
};

const DiffViewer = memo(({ file }: { file: any }) => {
  const { token } = theme.useToken();

  return (
    <div key={file.oldPath + "-" + file.newPath}>
      <Typography.Text strong style={{ ...mono.style, fontSize: "14px" }}>
        {file.oldPath || file.newPath || "/dev/null"}
      </Typography.Text>
      <div
        className="diff-file"
        style={{
          border: `1px solid ${token.colorBorder}`,
          borderRadius: 4,
          overflow: "hidden",
          marginTop: 8,
          backgroundColor: token.colorBgContainer,
        }}
      >
        <style jsx global>{`
          .diff-file {
            font-family: ${mono.style.fontFamily};
            font-size: 13px;
          }
          .diff {
            width: 100%;
            border-spacing: 0;
          }
          .diff-gutter-col {
            width: 50px;
            color: ${token.colorTextSecondary};
            background-color: ${token.colorFillTertiary};
            text-align: right;
            padding: 0 8px;
          }
          .diff-gutter-omit {
            height: 8px;
          }
          .diff-code {
            padding: 0 8px;
            white-space: pre;
          }
          .diff-code-delete {
            background-color: #ffeef0;
          }
          .diff-code-insert {
            background-color: #e6ffec;
          }
        `}</style>
        <Diff
          viewType="unified"
          diffType={file.type || "modify"}
          hunks={file.hunks}
        >
          {(hunks) =>
            hunks.map((hunk) => <Hunk key={hunk.content} hunk={hunk} />)
          }
        </Diff>
      </div>
    </div>
  );
});

DiffViewer.displayName = "DiffViewer";

export const CommitEntity = () => {
  const { token } = theme.useToken();

  const diffStyles = {
    ".diff-file": {
      fontFamily: mono.style.fontFamily,
      fontSize: "13px",
    },
    ".diff": {
      width: "100%",
      borderSpacing: 0,
    },
    ".diff-gutter-col": {
      width: "50px",
      color: token.colorTextSecondary,
      backgroundColor: token.colorFillTertiary,
      textAlign: "right" as const,
      padding: "0 8px",
    },
    ".diff-gutter-omit": {
      height: "8px",
    },
    ".diff-code": {
      padding: "0 8px",
      whiteSpace: "pre" as const,
    },
    ".diff-code-delete": {
      backgroundColor: "#ffeef0",
    },
    ".diff-code-insert": {
      backgroundColor: "#e6ffec",
    },
  };

  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entity = context.dataRef.current.entity.properties;
  const entityType = context.dataRef.current.entity.labels[0];

  const {
    filesAdded,
    filesModified,
    filesDeleted,
    message,
    repository,
    sourceUrl,
    uuid,
    vzblAtomicity,
    vzblAtomicityExplanation,
    vzblMaintainability,
    vzblMaintainabilityExplanation,
    vzblQuality,
    vzblQualityExplanation,
    vzblComplexity,
    vzblComplexityExplanation,
    vzblMessageClarityScore,
    vzblMessageClarityExplanation,
    vzblRiskScore,
    vzblRiskScoreExplanation,
    vzblTechStackLayers,
    vzblTechStackLayerExplanations,
    vzblSummary,
    vzblValueStatement,
  } = entity;

  const headerAttributes: HeaderAttribute[] = [
    {
      value: "created by",
      child: (
        <RelatedEntity
          orgId={orgId}
          entityUuid={uuid}
          relation={`${actSchema.Person.Commit.Created}`}
          direction="inverse"
        >
          <RelatedVzblTag asLink />
        </RelatedEntity>
      ),
    },
    {
      value: "repository",
      child: (
        <VzblTag
          asLink
          orgId={orgId}
          context={context}
          text={repository}
          color="none"
          linkParams={{
            orgId,
            entityType: entityType,
            attribute: "repository",
            value: repository,
          }}
        />
      ),
    },
  ];

  const workTabsItems: TabbedWorkItems[] = generateWorkTabs(
    orgId,
    entity.uuid,
    [
      { entityTypes: EntityTypes.Issue },
      { entityTypes: EntityTypes.Commit },
      { entityTypes: EntityTypes.PullRequest, label: "Pull Requests" },
      { entityTypes: EntityTypes.CicdPipeline, label: "Deployments" },
    ]
  );

  const allFilesAndTypes = [
    ...coerceArray(filesAdded).map((file: string) => ({
      file: file,
      attribute: "filesAdded",
    })),
    ...coerceArray(filesDeleted).map((file: string) => ({
      file: file,
      attribute: "filesDeleted",
    })),
    ...coerceArray(filesModified).map((file: string) => ({
      file: file,
      attribute: "filesModified",
    })),
  ];

  const ListRow: React.FC<ListChildComponentProps> = ({ index, style }) => (
    <Row key={allFilesAndTypes[index].file} style={style}>
      <Col span={1}>
        {allFilesAndTypes[index].attribute === "filesAdded" && (
          <FileAddOutlined style={{ color: "green" }} />
        )}
        {allFilesAndTypes[index].attribute === "filesDeleted" && (
          <DeleteOutlined style={{ color: "red" }} />
        )}
        {allFilesAndTypes[index].attribute === "filesModified" && (
          <EditOutlined />
        )}
      </Col>
      <Col span={23}>
        <VzblTag
          orgId={orgId}
          context={context}
          text={allFilesAndTypes[index].file}
          color="none"
          linkParams={{
            orgId,
            entityType: entityType,
            attribute: allFilesAndTypes[index].attribute,
            value: allFilesAndTypes[index].file,
          }}
        />
      </Col>
    </Row>
  );

  const tabs = [
    {
      key: "overview",
      id: "overview",
      label: "Overview",
      children: <Insights entity={context.dataRef.current.entity} />,
    },
    {
      key: "2",
      id: "2",
      label: "Work",
      children: <TabbedWork items={workTabsItems} />,
    },
    {
      key: "3",
      id: "3",
      label: "Change Set",
      children: (
        <>
          <div
            style={{ height: Math.min(600, allFilesAndTypes.length * 32 + 32) }}
          >
            <AutoSizer>
              {({ width, height }) => (
                <List
                  height={height}
                  itemCount={allFilesAndTypes.length}
                  itemSize={32}
                  width={width}
                >
                  {ListRow}
                </List>
              )}
            </AutoSizer>
          </div>
          {["{", "["].includes(
            context.dataRef.current.entity.properties.diffData
              ?.toString()
              ?.at(0)
          ) && (
            <div>
              <Typography.Title level={5}>Diff data</Typography.Title>
              {(() => {
                try {
                  const diffData = JSON.parse(
                    context.dataRef.current.entity.properties.diffData
                  );

                  // Convert the GitHub-style patch format to unified diff format
                  const unifiedDiff = formatPatchToUnifiedDiff(diffData);

                  if (!unifiedDiff) {
                    return (
                      <Typography.Text type="warning">
                        No diff data available
                      </Typography.Text>
                    );
                  }

                  const files = parseDiff(unifiedDiff);

                  if (!files || files.length === 0) {
                    return (
                      <Typography.Text type="warning">
                        No changes found in diff
                      </Typography.Text>
                    );
                  }

                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "24px",
                      }}
                    >
                      {files.map((file) => {
                        return (
                          <DiffViewer
                            key={file.oldPath + "-" + file.newPath}
                            file={file}
                          />
                        );
                      })}
                    </div>
                  );
                } catch (error: unknown) {
                  return (
                    <Typography.Text type="danger">
                      Error parsing diff data: {(error as Error).message}
                    </Typography.Text>
                  );
                }
              })()}
            </div>
          )}
        </>
      ),
    },
    {
      key: "5",
      id: "5",
      label: "History",
      children: (
        <History>
          <HistoryComponent />
        </History>
      ),
    },
  ];

  return (
    <EntityScreen
      headerPanel={
        <HeaderPanel
          entity={context.dataRef.current.entity}
          attributes={headerAttributes}
          icon={<GithubIcon size={40} />}
          label={sourceUrl
            ?.split("/")
            [sourceUrl.split("/").length - 1].slice(0, 7)}
        />
      }
      summaryPanel={<SummaryPanel title={message} description={vzblSummary} />}
      insightsPanel={
        <InsightsPanel
          cards={[
            vzblValueStatement ? (
              <ActionCard
                badge={<SummaryTag />}
                label="Value Statement"
                value={<SentencesList showNa data={vzblValueStatement} />}
              />
            ) : undefined,
            vzblTechStackLayers !== undefined && (
              <ActionCard
                key="techstack"
                badge={<SummaryTag />}
                label="Tech Stack Layers"
                value={
                  <div>
                    <TagsList
                      tags={(vzblTechStackLayers as string[]) || []}
                      threshold={2}
                      color="default"
                      tag={(tag, index) => (
                        <VzblTag
                          orgId={orgId}
                          context={context}
                          text={tag}
                          color="none"
                          key={index}
                          linkParams={{
                            orgId,
                            entityType: entityType,
                            attribute: "vzblTechStackLayers",
                            value: tag,
                          }}
                        />
                      )}
                    />
                    <Spacer height={10} />
                    <SentencesList data={vzblTechStackLayerExplanations} />
                  </div>
                }
              />
            ),
            vzblMaintainability ? (
              <ActionCard
                key="maintainability"
                badge={<MetricTag />}
                label="Maintainability"
                value={
                  <ProgressMetric
                    higherBetter
                    value={vzblMaintainability}
                    tooltip={vzblMaintainabilityExplanation}
                  />
                }
              />
            ) : undefined,
            vzblQuality ? (
              <ActionCard
                key="quality"
                badge={<MetricTag />}
                label="Quality"
                value={
                  <ProgressMetric
                    higherBetter
                    value={vzblQuality}
                    tooltip={vzblQualityExplanation}
                  />
                }
              />
            ) : undefined,
            vzblComplexity ? (
              <ActionCard
                key="complexity"
                badge={<MetricTag />}
                label="Complexity"
                value={
                  <ProgressMetric
                    lowerBetter
                    value={vzblComplexity}
                    tooltip={vzblComplexityExplanation}
                  />
                }
              />
            ) : undefined,
            vzblAtomicity ? (
              <ActionCard
                key="atomicity"
                badge={<MetricTag />}
                label="Atomicity"
                value={
                  <ProgressMetric
                    lowerBetter
                    value={vzblAtomicity}
                    data={vzblAtomicityExplanation}
                  />
                }
              />
            ) : undefined,
            vzblMessageClarityScore ? (
              <ActionCard
                key="clarity"
                badge={<MetricTag />}
                label="Clarity"
                value={
                  <ProgressMetric
                    higherBetter
                    value={vzblMessageClarityScore}
                    tooltip={vzblMessageClarityExplanation}
                  />
                }
              />
            ) : undefined,
            vzblRiskScore ? (
              <ActionCard
                key="risk-score"
                badge={<MetricTag />}
                label="Risk Score"
                value={
                  <ProgressMetric
                    lowerBetter
                    value={vzblRiskScore}
                    data={vzblRiskScoreExplanation}
                  />
                }
              />
            ) : undefined,
          ].filter(Boolean)}
        />
      }
    >
      <EntityTabs
        propertiesSkipList={[
          "authorName",
          "detailsUrl",
          "repository",
          "name",
          "message",
          "vzblSummary",
          "vzblSummaryChangeset",
          "vzblSummaryPurpose",
          "vzblComplexity",
          "vzblComplexityExplanation",
          "vzblMaintainability",
          "vzblMaintainabilityExplanation",
          "vzblMessageClarityScore",
          "vzblMessageClarityExplanation",
          "vzblRiskScore",
          "vzblRiskExplanation",
          "vzblQuality",
          "vzblQualityExplanation",
          "vzblTechStackLayers",
          "vzblAtomicity",
          "vzblAtomicityExplanation",
          "vzblTechStackLayerExplanations",
          "vzblTechDebt", //legacy, should remove at some point, replaced by quality
          "vzblTechDebtExplanation", //legacy, should remove at some point, replaced by quality
        ]}
        entity={context.dataRef.current.entity}
        tabs={tabs}
      />
    </EntityScreen>
  );
};
